/**
 * wrapperFramerMotion
 * 针对低版本IE相关的处理，IE11及以下直接屏蔽相关接口
 *
 * Created by Samuel on 2022/6/10
 */
import { noop } from 'lodash-es';
import { Fragment, forwardRef, memo, useEffect, useRef, useState } from 'react';

import { htmlElements } from '@core/utils/htmlElements';

function baseComponent(Cp) {
	const UnMotion = forwardRef(
		(
			{ initial, animate, transition, variants, layoutId, whileHover, whileTap, style, onAnimationComplete, ...props },
			ref,
		) => {
			useEffect(() => {
				onAnimationComplete && onAnimationComplete();
			}, []);
			let objAnimate = animate;
			// is string
			if (typeof animate === 'string') {
				if (variants) {
					objAnimate = variants[animate];
					// remove transition info
					delete objAnimate.transition;
				}
				objAnimate = {};
			}

			let _style = style || objAnimate;
			if (style && objAnimate) {
				_style = { ...style, ...objAnimate };
			}
			return <Cp {...props} style={_style} ref={ref} />;
		},
	);
	UnMotion.displayName = `Motion(${Cp})`;
	return memo(UnMotion);
}

function noMotion() {
	let _noMotionComponent = (a) => a;

	_noMotionComponent = htmlElements.reduce((acc, key) => {
		acc[key] = baseComponent(key);
		return acc;
	}, _noMotionComponent);

	return _noMotionComponent;
}

let loaded = false;
let usePresence = () => [true, noop];
let useIsPresent = () => true;
let motion = noMotion();
let AnimatePresence = memo(function _AnimatePresence({ onExitComplete, children }) {
	const prevChildrenRef = useRef();
	useEffect(() => {
		if (prevChildrenRef.current && !children) {
			onExitComplete && onExitComplete();
		}
		prevChildrenRef.current = children;
	}, [children]);
	return <>{children}</>;
});
let LayoutGroup = Fragment;
let MotionConfig = Fragment;

export const lazyMotion = () => {
	return import(/* webpackChunkName: "motion" */ 'framer-motion').then(
		({
			motion: _motion,
			AnimatePresence: _AnimatePresence,
			LayoutGroup: _LayoutGroup,
			MotionConfig: _MotionConfig,
			...ext
		}) => {
			motion = _motion;
			AnimatePresence = _AnimatePresence;
			LayoutGroup = _LayoutGroup;
			MotionConfig = _MotionConfig;
			usePresence = ext.usePresence;
			useIsPresent = ext.useIsPresent;
			loaded = true;

			_updateMotionifyComponent();
		},
	);
};

// 载入成功后，手动更新所有的motion化的组件
// 不使用useMemo的模式，是因为文档中的 In addition, motion() must not be called inside a React render function.
// ref: https://www.framer.com/docs/component/#custom-components
function _updateMotionifyComponent() {
	_CACHED_MOTION_COMPONENTS.forEach((data, Component) => {
		data = {
			...data,
			cp: motion(Component),
		};
		_CACHED_MOTION_COMPONENTS.set(Component, data);
		data.callbacks.forEach((cb) => {
			cb(data.cp);
		});
	});
}
const _CACHED_MOTION_COMPONENTS = new Map();
const _getCacheListener = (component) => {
	if (_CACHED_MOTION_COMPONENTS.has(component)) {
		return _CACHED_MOTION_COMPONENTS.get(component).callbacks;
	}
	return null;
};
const _updateCacheListener = (component, listeners) => {
	if (_CACHED_MOTION_COMPONENTS.has(component)) {
		const data = _CACHED_MOTION_COMPONENTS.get(component);
		data.callbacks = listeners;
		_CACHED_MOTION_COMPONENTS.set(component, data);
	}
};
export const motionify = (Component) => {
	if (!_CACHED_MOTION_COMPONENTS.has(Component)) {
		_CACHED_MOTION_COMPONENTS.set(Component, { cp: motion(Component), callbacks: [] });
	}
	const MotionComponent = forwardRef(function MotionComponent(props, forwardedRef) {
		const [MComponent, setComponent] = useState(() => _CACHED_MOTION_COMPONENTS.get(Component).cp);

		useEffect(() => {
			if (loaded) {
				return () => {};
			}
			const callback = (newMComponent) => {
				setComponent(newMComponent);
			};
			const listeners = _getCacheListener(Component);
			_updateCacheListener(Component, [...listeners, callback]);

			return () => {
				const listeners = _getCacheListener(Component);
				const idx = listeners.indexOf(callback);
				if (idx > -1) {
					listeners.slice().splice(idx, 1);
					_updateCacheListener(Component, listeners);
				}
			};
		}, []);

		return <MComponent {...props} ref={forwardedRef} />;
	});

	return memo(MotionComponent);
};

export { motion, AnimatePresence, LayoutGroup, MotionConfig, usePresence, useIsPresent };
